/**
 * Ordergroove – Sticky bar price summary
 *
 * Shows a read-only price line inside the sticky quick-buy bar that
 * mirrors the subscription state chosen in the main PDP widget:
 *
 *   Subscribed  →  $44.99  $52.99  / 4 weeks
 *   One-time    →  $52.99 / One Time
 *
 * The element hides the default <price-list> and replaces it with its
 * own markup.  It re-renders automatically whenever the customer
 * toggles the Ordergroove opt-in on the PDP.
 *
 * Usage (Liquid):
 *   <og-sticky-toggle
 *     data-variant-id="{{ variant.id }}"
 *     data-variant-price="{{ variant.price }}"
 *     data-money-format="{{ shop.money_format | escape }}"
 *     hidden
 *   ></og-sticky-toggle>
 */
(() => {
  const POLL_INTERVAL = 250; // ms between readiness checks
  const MAX_POLL      = 40;  // give up after 10 s

  /* ── Shopify money formatter ────────────────────────────────────── */

  function formatMoney(cents, format) {
    const amount = (cents / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.ceil(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, String(Math.ceil(cents / 100)));
  }

  /* ── OG state readers ───────────────────────────────────────────── */

  /** Safely return the Ordergroove store snapshot (or null). */
  function ogState() {
    try { return OG?.store?.getState() ?? null; } catch { return null; }
  }

  /** Look up a value in an OG hash that may be keyed by string OR number. */
  function ogLookup(hash, variantId) {
    if (!hash) return undefined;
    return hash[variantId] ?? hash[Number(variantId)];
  }

  /** True when Ordergroove considers this variant subscription-eligible. */
  function isEligible(state, variantId) {
    return ogLookup(state?.autoshipEligible, variantId) === true;
  }

  /** True when the customer has currently opted IN to subscribe. */
  function isOptedIn(state, variantId) {
    if (!state) return false;
    if (state.optedin?.some(e => String(e.id) === String(variantId))) return true;
    if (state.optedout?.some(e => String(e.id) === String(variantId))) return false;
    return ogLookup(state.autoshipByDefault, variantId) ?? false;
  }

  /**
   * Return the first available incentive object ({ type, value }) for
   * the given variant.  Falls back to any product-level incentive
   * because Ordergroove often stores programme-wide discounts under a
   * single (sometimes arbitrary) variant key.
   */
  function getIncentive(state, variantId) {
    if (!state?.incentives) return null;

    const extract = (bucket) => bucket?.initial?.[0] || bucket?.ongoing?.[0];

    // Try variant-specific first
    const directIncentive = extract(ogLookup(state.incentives, variantId));
    if (directIncentive?.value) return directIncentive;

    // Fall back to any available incentive (programme-wide discount)
    for (const key of Object.keys(state.incentives)) {
      const fallback = extract(state.incentives[key]);
      if (fallback?.value) return fallback;
    }
    return null;
  }

  /** Apply the incentive to the original price and return discounted cents. */
  function calcDiscountedPrice(priceCents, incentive) {
    if (!incentive) return null;
    const value = parseFloat(incentive.value);
    if (isNaN(value) || value <= 0) return null;

    const type = (incentive.type || '').toLowerCase();
    if (type.includes('percent'))     return Math.ceil(priceCents * (1 - value / 100));
    if (type.includes('fixed'))       return Math.ceil(priceCents - value * 100);
    return null;
  }

  /** Return the currently active frequency id for the variant. */
  function getActiveFrequency(state, variantId) {
    if (!state) return null;
    const entry = state.optedin?.find(e => String(e.id) === String(variantId));
    if (entry) return entry.frequency;
    const productFreqs = ogLookup(state.config?.productFrequencies, variantId);
    return productFreqs?.defaultFrequency || state.config?.defaultFrequency || null;
  }

  /** Translate a frequency id into its human-readable label (e.g. "4 weeks"). */
  function getFrequencyLabel(state, variantId, frequencyId) {
    const productFreqs = ogLookup(state?.config?.productFrequencies, variantId);
    if (!productFreqs) return null;
    const index = productFreqs.frequencies?.indexOf(frequencyId);
    if (index == null || index < 0) return productFreqs.frequenciesText?.[0] ?? null;
    return productFreqs.frequenciesText?.[index] ?? null;
  }

  /* ── Custom element ─────────────────────────────────────────────── */

  class OgStickyToggle extends HTMLElement {

    connectedCallback() {
      this._variantId    = this.dataset.variantId;
      this._moneyFormat  = this.dataset.moneyFormat;
      this._variantPrice = parseInt(this.dataset.variantPrice, 10) || 0;
      this._oneTimeLabel = this.dataset.oneTimeLabel || 'One Time';
      this._discountedPrice = null;
      this._pollCount = 0;
      this._pollTimer = setInterval(() => this._pollForOG(), POLL_INTERVAL);
    }

    disconnectedCallback() {
      clearInterval(this._pollTimer);
    }

    /**
     * Wait until the Ordergroove SDK has loaded AND populated its
     * eligibility data before initialising.  The SDK may exist before
     * its async data (autoshipEligible) is ready, so we keep polling.
     */
    _pollForOG() {
      if (++this._pollCount > MAX_POLL) {
        clearInterval(this._pollTimer);
        return;
      }

      const state = ogState();
      if (!state || typeof OG === 'undefined') return;

      // autoshipEligible is populated asynchronously — wait for it
      const eligible = state.autoshipEligible;
      if (!eligible || Object.keys(eligible).length === 0) return;

      if (!isEligible(state, this._variantId)) {
        clearInterval(this._pollTimer);
        return;
      }

      clearInterval(this._pollTimer);
      this._activate(state);
    }

    /** First render + start listening for PDP opt-in changes. */
    _activate(state) {
      this._discountedPrice = calcDiscountedPrice(
        this._variantPrice,
        getIncentive(state, this._variantId),
      );

      this._renderPrice(state);
      this.removeAttribute('hidden');

      // Hide the default <price-list> that sits above us
      const wrapper   = this.closest('.og-sticky-info');
      const priceList = wrapper?.previousElementSibling;
      if (priceList?.tagName === 'PRICE-LIST') {
        priceList.style.display = 'none';
      }

      // Re-render whenever the customer toggles subscribe on the PDP
      OG.addOptinChangedCallback((event) => {
        if (String(event.productId) !== String(this._variantId)) return;
        this._renderPrice(ogState(), event.frequency);
      });
    }

    /** (Re-)render the price line based on current OG state. */
    _renderPrice(state, overrideFrequency) {
      const subscribed    = isOptedIn(state, this._variantId);
      const frequencyId   = overrideFrequency || getActiveFrequency(state, this._variantId);
      const frequencyText = getFrequencyLabel(state, this._variantId, frequencyId);
      const fmt           = this._moneyFormat;

      if (subscribed && this._discountedPrice != null) {
        this.innerHTML = [
          `<span class="og-sticky-sub-price">${formatMoney(this._discountedPrice, fmt)}</span>`,
          `<span class="og-sticky-original-price">${formatMoney(this._variantPrice, fmt)}</span>`,
          `<span class="og-sticky-freq">/ ${frequencyText || 'month'}</span>`,
        ].join(' ');
      } else {
        this.innerHTML = [
          `<span class="og-sticky-sub-price">${formatMoney(this._variantPrice, fmt)}</span>`,
          `<span class="og-sticky-freq">/ ${this._oneTimeLabel}</span>`,
        ].join(' ');
      }
    }
  }

  if (!customElements.get('og-sticky-toggle')) {
    customElements.define('og-sticky-toggle', OgStickyToggle);
  }
})();
